import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { getProducts } from '../../services/ProductService';
import { getSupplies } from '../../services/SuppliesService';
import { getLocations } from '../../services/LocationService';
import { getReceivedInvoiceItemMappings, saveReceivedInvoiceItemLinks } from '../../services/ElectronicBillingService';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { formatMoney } from '../../utils/currency';
import { getUnitById, unitOptions } from '../../utils/Enums';
import { bridgesMassAndVolume, convertQuantity, defaultOrigin } from '../../utils/receivedInvoiceUnits';

const normalizeItemValue = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Supplier codes survive a rename in the issuer's system; the description is only
// used when the electronic document does not provide one.
const itemKey = (item) => {
  const externalItemId = normalizeItemValue(item?.externalItemId);
  return externalItemId ? `external:${externalItemId}` : `description:${normalizeItemValue(item?.description)}`;
};

// Prices in the system include IVA: suppliers that bill net (grossAmounts=false) get the line rate added.
const lineUnitPrice = (item, grossAmounts = true) => {
  const base = lineNetUnitPrice(item);
  if (base == null || grossAmounts) return base;
  return base * (1 + (Number(item?.ivaRate) || 0) / 100);
};

const lineNetUnitPrice = (item) => {
  const quantity = Number(item?.quantity);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (item?.amount != null && Number.isFinite(Number(item.amount))) return Number(item.amount) / quantity;
  if (item?.unitPrice != null && Number.isFinite(Number(item.unitPrice))) {
    const discount = item.discountAmount != null
      ? Number(item.discountAmount) / quantity
      : item.discountPercent != null
        ? Number(item.unitPrice) * Number(item.discountPercent) / 100
        : 0;
    return Number(item.unitPrice) - discount;
  }
  return null;
};

const nameWords = (value) => normalizeItemValue(value).split(/[^a-z0-9áéíóúñ]+/).filter((word) => word.length > 2);

// Cheap "did you mean": the supply sharing the most words with the invoice description, if any.
const suggestSupply = (description, supplies) => {
  const wanted = nameWords(description);
  if (!wanted.length) return null;
  let best = null;
  let bestScore = 0;
  supplies.forEach((supply) => {
    const words = new Set(nameWords(supply?.name));
    const score = wanted.filter((word) => words.has(word)).length / Math.max(wanted.length, words.size);
    if (score > bestScore) { best = supply; bestScore = score; }
  });
  return bestScore >= 0.5 ? best : null;
};

const formatNumber = (value) => Number(Number(value).toFixed(4)).toLocaleString('es-UY');

const visibleInvoiceItems = (invoice) => (invoice?.items || []).filter((item) => itemKey(item) !== 'description:');

const ReceivedInvoiceSyncDialog = ({ invoice, locationId, onClose, onSynced }) => {
  const invoiceItems = useMemo(() => visibleInvoiceItems(invoice), [invoice]);
  const [supplies, setSupplies] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [mappings, setMappings] = useState({});
  const options = useMemo(() => [
    ...supplies.map((supply) => ({ ...supply, referenceType: 1 })),
    ...products.map((product) => ({ ...product, referenceType: 4 })),
  ], [supplies, products]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!invoice || !locationId) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [suppliesResponse, productsResponse, locationsResponse, savedMappings] = await Promise.all([
          getSupplies(locationId, 1, 10000),
          getProducts(locationId, 1, 10000),
          getLocations(),
          getReceivedInvoiceItemMappings({
            supplierRut: invoice.rucEmisor,
            items: invoiceItems.map((item) => ({
              externalItemId: item.externalItemId,
              description: item.description,
            })),
          }),
        ]);
        if (!active) return;
        const availableSupplies = Array.isArray(suppliesResponse)
          ? suppliesResponse
          : suppliesResponse?.supplies || suppliesResponse?.data || [];
        const availableProducts = Array.isArray(productsResponse)
          ? productsResponse
          : productsResponse?.products || productsResponse?.data || [];
        const savedByKey = (savedMappings || []).reduce((result, mapping) => ({
          ...result,
          [mapping.itemKey]: {
            referenceType: mapping.referenceType,
            referenceId: mapping.referenceId,
            quantityFactor: String(mapping.quantityFactor ?? 1),
            destinationId: mapping.locationId ?? null,
          },
        }), {});
        const defaults = invoiceItems.reduce((result, item) => ({
          ...result,
          [itemKey(item)]: savedByKey[itemKey(item)] || {
            referenceType: 1,
            referenceId: null,
            quantityFactor: '1',
          },
        }), {});
        setLocations(locationsResponse || []);
        setSupplies(availableSupplies);
        setProducts(availableProducts);
        setMappings(defaults);
      } catch (error) {
        console.error('Error loading received invoice sync options', error);
        SnackbarUtils.error('No se pudieron cargar los insumos y productos para vincular.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [invoice, invoiceItems, locationId]);

  // Supply chosen: guess "origin" (what one invoice unit brings) from the description, e.g. "Harina 25kg".
  // The user can always correct quantity and origin unit; only the resulting factor is stored.
  const selectTarget = (key, item, target) => {
    if (!target) {
      updateMapping(key, { referenceType: 1, referenceId: null, quantityFactor: '1', originQty: undefined, originUnit: undefined, detected: false });
      return;
    }
    if (target.referenceType === 4) {
      updateMapping(key, { referenceType: 4, referenceId: target.id, quantityFactor: '1', originQty: undefined, originUnit: undefined, detected: false });
      return;
    }
    const origin = defaultOrigin(item.description, target.unit, target.conversionFactor);
    updateMapping(key, {
      referenceType: 1,
      referenceId: target.id,
      ...origin,
      quantityFactor: String(convertQuantity(origin.originQty, origin.originUnit, target.unit, target.conversionFactor)),
    });
  };

  const changeOrigin = (key, supplyUnit, density, changes, current) => {
    const originQty = changes.originQty ?? current.originQty;
    const originUnit = changes.originUnit ?? current.originUnit;
    const factor = convertQuantity(originQty, originUnit, supplyUnit, density);
    updateMapping(key, { originQty, originUnit, detected: false, quantityFactor: factor == null ? '' : String(factor) });
  };

  const updateMapping = (key, changes) => {
    setMappings((previous) => ({
      ...previous,
      [key]: { ...previous[key], ...changes },
    }));
  };

  // Linking is partial: only lines the user picked a target for are saved, the rest stay unlinked.
  const linkedItems = invoiceItems.filter((item) => mappings[itemKey(item)]?.referenceId);
  const canSync = linkedItems.length > 0
    && linkedItems.every((item) => Number(mappings[itemKey(item)].quantityFactor) > 0);

  const handleSync = async () => {
    if (!canSync || !invoice) return;
    setSaving(true);
    try {
      // A supplier-level view has no single invoice: nothing to mark as already handled.
      const invoiceKey = invoice.synthetic ? null : (invoice.id || `${invoice.rucEmisor}:${invoice.tipoCfe}:${invoice.serie}:${invoice.nro}`);
      const result = await saveReceivedInvoiceItemLinks({
        supplier_rut: invoice.rucEmisor,
        invoice_key: invoiceKey ? String(invoiceKey) : null,
        items: linkedItems.map((item) => {
          const mapping = mappings[itemKey(item)];
          return {
            description: item.description,
            external_item_id: item.externalItemId || null,
            reference_type: Number(mapping.referenceType),
            reference_id: Number(mapping.referenceId),
            quantity_factor: Number(mapping.quantityFactor),
            // Null = the location of the invoice RUT.
            location_id: mapping.destinationId && Number(mapping.destinationId) !== Number(locationId) ? Number(mapping.destinationId) : null,
          };
        }),
      });
      SnackbarUtils.success(
        linkedItems.length === 1
          ? 'Vínculo guardado. Las próximas facturas de este proveedor generarán el pedido pendiente.'
          : `${linkedItems.length} vínculos guardados. Las próximas facturas de este proveedor generarán el pedido pendiente.`
      );
      onSynced?.(result);
      onClose();
    } catch (error) {
      console.error('Error linking received invoice items', error);
      SnackbarUtils.error(error.response?.data?.error_message || 'No se pudieron guardar los vínculos. Verificá que el proveedor exista con ese RUT.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(invoice)} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{invoice?.synthetic ? 'Ítems sin vincular del proveedor' : 'Vincular líneas de la factura'}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Vinculá las líneas que quieras; podés dejar el resto sin vincular. Esto no genera ningún pedido ni mueve stock.
          Cuando llegue una nueva factura de este proveedor con líneas vinculadas, aparecerá en Reposición › Pendientes con los ítems y cantidades precargados para que los revises.
        </Alert>
        {invoice && !invoice.synthetic && !invoice.supplierRegistered && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            El emisor de esta factura no está cargado como proveedor. Crealo en Proveedores con este RUT para poder vincular sus ítems.
          </Alert>
        )}
        {!locationId && <Alert severity="error">No se encontró la sucursal asociada a este RUT.</Alert>}
        {!invoiceItems.length && <Alert severity="warning">Esta factura no incluye líneas para vincular.</Alert>}
        {invoiceItems.map((item, index) => {
          const key = itemKey(item);
          const mapping = mappings[key] || {};
          const isProduct = Number(mapping.referenceType) === 4;
          const selected = mapping.referenceId
            ? options.find((option) => option.referenceType === Number(mapping.referenceType) && Number(option.id) === Number(mapping.referenceId)) || null
            : null;
          const suggestion = !selected ? suggestSupply(item.description, supplies) : null;
          const unit = selected && !isProduct ? getUnitById(selected.unit).label : null;
          const factor = Number(mapping.quantityFactor);
          const originQty = mapping.originQty ?? mapping.quantityFactor ?? 1;
          const originUnit = mapping.originUnit ?? selected?.unit ?? 5;
          return (
            <Box key={`${key}-${index}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.6fr' }, gap: 1.5, alignItems: 'start', mb: 2 }}>
              <Box>
                <Typography variant="body2" fontWeight={700}>{item.description}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.quantity} unidades · {formatMoney(lineUnitPrice(item, invoice?.grossAmounts !== false))} por unidad de factura{invoice?.grossAmounts === false && Number(item.ivaRate) > 0 ? ` (IVA ${item.ivaRate}% incluido)` : ''}
                </Typography>
                {item.externalItemId && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Código proveedor: {item.externalItemId}
                  </Typography>
                )}
              </Box>
              <Box>
                <Autocomplete
                  options={options}
                  groupBy={(option) => (option.referenceType === 4 ? 'Productos' : 'Insumos')}
                  value={selected}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option.referenceType === value.referenceType && option.id === value.id}
                  onChange={(_, value) => selectTarget(key, item, value)}
                  renderInput={(params) => <TextField {...params} label="Vincular con (opcional)" size="small" />}
                  loading={loading}
                />
                {suggestion && (
                  <Chip
                    size="small"
                    sx={{ mt: 0.5 }}
                    label={`¿Es "${suggestion.name}"?`}
                    onClick={() => selectTarget(key, item, { ...suggestion, referenceType: 1 })}
                  />
                )}
                {selected && locations.length > 1 && (
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Destino de la reposición"
                    value={mapping.destinationId ?? locationId}
                    onChange={(event) => updateMapping(key, { destinationId: Number(event.target.value) })}
                    sx={{ mt: 1 }}
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        {location.name}{location.isPlant ? ' (planta)' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {selected && !isProduct && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">1 de la factura trae</Typography>
                    <TextField
                      type="number"
                      size="small"
                      value={originQty}
                      onChange={(event) => changeOrigin(key, selected.unit, selected.conversionFactor, { originQty: event.target.value }, { originQty, originUnit })}
                      inputProps={{ min: 0.0001, step: 'any' }}
                      sx={{ width: 100 }}
                    />
                    <TextField
                      select
                      size="small"
                      value={originUnit}
                      onChange={(event) => changeOrigin(key, selected.unit, selected.conversionFactor, { originUnit: Number(event.target.value) }, { originQty, originUnit })}
                      sx={{ width: 110 }}
                    >
                      {unitOptions.map((option) => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                    </TextField>
                    <Typography variant="caption">→ se guarda en {unit}</Typography>
                    {mapping.detected && <Chip size="small" color="info" variant="outlined" label="Detectado del nombre" />}
                  </Box>
                )}
                {selected && !isProduct && factor > 0 && bridgesMassAndVolume(originUnit, selected.unit) && (
                  <Typography variant="caption" color="info.main" display="block" sx={{ mt: 0.5 }}>
                    Convertido con el factor de conversión del insumo: 1 {getUnitById(originUnit).label} = {formatNumber(convertQuantity(1, originUnit, selected.unit, selected.conversionFactor))} {unit}. Si no es correcto, corregilo en el insumo.
                  </Typography>
                )}
                {selected && !isProduct && !(factor > 0) && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                    No se puede convertir {getUnitById(originUnit).label} a {unit}. Elegí una unidad compatible.
                  </Typography>
                )}
                {selected && factor > 0 && Number(item.quantity) > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Esta factura: {formatNumber(item.quantity)} × {formatNumber(factor)} = {formatNumber(Number(item.quantity) * factor)} {unit || 'unidades'}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <LoadingButton variant="contained" onClick={handleSync} loading={saving} disabled={loading || !locationId || !canSync || (!invoice?.synthetic && !invoice?.supplierRegistered)}>
          Guardar vínculos
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ReceivedInvoiceSyncDialog;
